"use client"

// src/components/LoginModal/LoginModal.js
import { useState } from "react"
import "./LoginModal.css"
import { FaEnvelope, FaRegTimesCircle, FaArrowLeft, FaLock, FaPaperPlane, FaCheckCircle } from "react-icons/fa"
import googleIcon from "../../assets/icons/google.png"
import LoginImage from "../../assets/images/Loginimg.png"
import { signInWithGoogle } from "../../firebase/auth"
import { authStateChanged } from "../Header/Header"
import logger from "../../utils/logger"
import { API_URL } from "../../config"

// NOTE: The "Continue with Facebook" button was removed because the backend
// has no /api/auth/facebook-login endpoint — clicking it always failed.
// When we implement Facebook login on the backend, we can re-add the button.

// Success animation duration before closing the modal (keep it snappy)
const SUCCESS_DELAY_MS = 200

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const [loginStep, setLoginStep] = useState("initial") // "initial", "email", "otp"
  const [email, setEmail] = useState("")
  const [otp, setOtp] = useState(["", "", "", "", "", ""])
  const [isLoading, setIsLoading] = useState(false) // Controls the full-modal loader
  const [error, setError] = useState("")
  const [isSuccess, setIsSuccess] = useState(false)

  if (!isOpen) return null

  const handleLoginComplete = () => {
    // Dispatch auth state change event (Header + AuthContext both listen)
    window.dispatchEvent(authStateChanged)

    // Call the onLoginSuccess callback if provided
    if (onLoginSuccess) {
      onLoginSuccess()
    } else {
      onClose()
    }
  }

  const handleGoogleLogin = async () => {
    setIsLoading(true)
    setError("")
    try {
      logger.log("🔄 Attempting Google Login...")
      const data = await signInWithGoogle()
      if (!data || !data.token || !data.user) {
        throw new Error("Invalid server response. JWT or user missing.")
      }
      logger.log("✅ Google Login Successful")
      // Save token & user details in localStorage (AuthContext hydrates from these instantly)
      localStorage.setItem("token", data.token)
      localStorage.setItem("user", JSON.stringify(data.user))

      setIsSuccess(true)
      setTimeout(() => {
        handleLoginComplete()
      }, SUCCESS_DELAY_MS)
    } catch (error) {
      logger.error("❌ Google login error:", error.message || error)
      setError(`Google login failed: ${error.message || "Unknown error"}`)
      setIsSuccess(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleEmailSubmit = async (e) => {
    e.preventDefault()
    setError("")

    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address")
      return
    }

    setIsLoading(true)
    try {
      const response = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.message || data.msg || "Failed to send OTP")

      logger.log("✅ OTP sent to email:", email)
      setLoginStep("otp")
    } catch (error) {
      logger.error("❌ Failed to send OTP:", error.message)
      setError(error.message || "Something went wrong. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleOtpChange = (index, value) => {
    if (value && !/^\d+$/.test(value)) return
    const newOtp = [...otp]
    newOtp[index] = value
    setOtp(newOtp)
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`)
      if (nextInput) nextInput.focus()
    }
  }

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`)
      if (prevInput) prevInput.focus()
    }
  }

  const handleOtpSubmit = async (e) => {
    e.preventDefault()
    setError("")
    const otpValue = otp.join("")
    if (otpValue.length !== 6) {
      setError("Please enter all 6 digits of the verification code")
      return
    }
    setIsLoading(true)
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp: otpValue }),
      })
      const data = await response.json()
      if (!response.ok || !data.token || !data.user) {
        throw new Error(data.message || data.msg || "Invalid OTP or server error")
      }
      // Save session info in localStorage (AuthContext hydrates from these instantly)
      localStorage.setItem("token", data.token)
      localStorage.setItem("user", JSON.stringify(data.user))
      setIsSuccess(true)
      logger.log("✅ OTP verified, login successful")

      setTimeout(() => {
        handleLoginComplete()
      }, SUCCESS_DELAY_MS)
    } catch (error) {
      logger.error("❌ OTP verification failed:", error.message)
      setError(error.message || "Invalid verification code. Please try again.")
      setIsSuccess(false)
    } finally {
      setIsLoading(false)
    }
  }

  const goBack = () => {
    if (loginStep === "otp") setLoginStep("email")
    else setLoginStep("initial")
    setError("")
    setIsSuccess(false)
    setOtp(["", "", "", "", "", ""])
  }

  const resendCode = async () => {
    setIsLoading(true)
    setError("")
    try {
      const response = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.message || data.msg || "Failed to resend OTP")
      setOtp(["", "", "", "", "", ""])
      logger.log("✅ OTP resent successfully.")
    } catch (error) {
      logger.error("❌ Failed to resend code:", error.message)
      setError("Failed to resend code. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const renderLoginContent = () => {
    switch (loginStep) {
      case "email":
        return (
          <>
            <div className="back-button-container">
              <button className="back-button" onClick={goBack} disabled={isLoading}>
                <FaArrowLeft /> Back
              </button>
            </div>
            <h2 className="mo">Enter your email address</h2>
            <p className="mo">We'll send a verification code to this email</p>
            <form onSubmit={handleEmailSubmit} className="email-form">
              <div className="input-container">
                <FaEnvelope className="input-icon" />
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="styled-input"
                  required
                  disabled={isLoading}
                />
              </div>
              {error && <p className="error-message">{error}</p>}
              <button type="submit" className="continue-button" disabled={isLoading}>
                Continue <FaPaperPlane className="button-icon" />
              </button>
            </form>
          </>
        )

      case "otp":
        return (
          <>
            <div className="back-button-container">
              <button className="back-button" onClick={goBack} disabled={isLoading}>
                <FaArrowLeft /> Back
              </button>
            </div>
            <h2 className="mo">Enter verification code</h2>
            <p className="mo">
              We've sent a 6-digit code to <strong>{email}</strong>
            </p>
            <form onSubmit={handleOtpSubmit} className="otp-form">
              <div className="otp-container">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-input-${index}`}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    className="otp-digit"
                    required
                    disabled={isLoading || isSuccess}
                  />
                ))}
              </div>
              {error && <p className="error-message">{error}</p>}
              <button
                type="submit"
                className={`continue-button ${isSuccess ? "success" : ""}`}
                disabled={isLoading || isSuccess}
              >
                {isSuccess ? (
                  <>
                    Success <FaCheckCircle className="button-icon" />
                  </>
                ) : (
                  <>
                    Verify & Login <FaLock className="button-icon" />
                  </>
                )}
              </button>
            </form>
            <p className="resend-code">
              Didn't receive the code?{" "}
              <button className="resend-button" onClick={resendCode} disabled={isLoading}>
                Resend
              </button>
            </p>
          </>
        )

      default:
        return (
          <>
            <h2 className="mo">Log in or sign up in seconds</h2>
            <p className="mo">
              Build skills for today, tomorrow, and beyond. (it's free)!{" "}
              <span style={{ color: "#47a5c5", fontFamily: '"Edu SA Beginner", cursive' }}>
                Education to future-proof your career.
              </span>
            </p>
            <div className="login-options">
              <button className="login-option" onClick={handleGoogleLogin} disabled={isLoading}>
                <img src={googleIcon || "/placeholder.svg"} alt="Google" className="login-icon" /> Continue with Google
              </button>
              <button className="login-option" onClick={() => setLoginStep("email")} disabled={isLoading}>
                <FaEnvelope className="login-icon" /> Continue with email
              </button>
            </div>
            <p className="terms">
              By continuing, you agree to inelf <a href="#terms">Terms of Use</a> and{" "}
              <a href="#privacy">Privacy Policy</a>.
            </p>
          </>
        )
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        {/* Loader Overlay */}
        {isLoading && (
          <div className="loader-overlay">
            <div className="loader-spinner"></div>
            <div className="loader-text">
              {loginStep === "email" ? "Sending OTP..." : "Processing request..."}
            </div>
          </div>
        )}

        <div className="modal-left">
          <button className="close-button" onClick={onClose} disabled={isLoading}>
            <FaRegTimesCircle size={24} />
          </button>
          {renderLoginContent()}
        </div>
        <div className="modal-right">
          <img src={LoginImage || "/placeholder.svg"} alt="Login" />
        </div>
      </div>
    </div>
  )
}

export default LoginModal